import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { notification } from "antd";
import { TestRunService } from "../../services/TestRunService";
import { TestSetService } from "../../services/TestSetService";
import { IntentService } from "../../services/IntentService";
import { usePermissions } from "../../PermissionsProvider";
import {
  TestSet,
  TestRun,
  TestRunRequest,
  TestRunQualitativeEvaluationSummary,
  IntentGroup,
} from "../../model_gen/evaluation";

export interface ProgressData {
  total_requests: number;
  completed_requests: number;
  error_requests: number;
  completed_evals: number;
  status: string;
}

export function useTestRunDetail() {
  const { intentGroupId, testSetId, testRunId } = useParams();
  const { apiKey } = usePermissions();
  const [intentGroup, setIntentGroup] = useState<IntentGroup | null>(null);
  const [testSet, setTestSet] = useState<TestSet | null>(null);
  const [testRun, setTestRun] = useState<TestRun | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [requests, setRequests] = useState<TestRunRequest[]>([]);
  const [isRequestsLoading, setIsRequestsLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [requestsByPage, setRequestsByPage] = useState<
    Record<number, TestRunRequest[]>
  >({});
  const [totalRequests, setTotalRequests] = useState(0);
  const [testRunResults, setTestRunResults] =
    useState<TestRunQualitativeEvaluationSummary | null>(null);
  const [isResultsLoading, setIsResultsLoading] = useState(false);
  const [selectedScores, setSelectedScores] = useState<number[]>([]);
  const [isComparisonModalVisible, setIsComparisonModalVisible] =
    useState(false);
  const [selectedComparisonRequestId, setSelectedComparisonRequestId] =
    useState<number | null>(null);
  const [selectedRequestId, setSelectedRequestId] = useState<string | null>(
    null
  );
  const [isRepresentationModalVisible, setIsRepresentationModalVisible] =
    useState(false);
  const [isConfigModalVisible, setIsConfigModalVisible] = useState(false);
  const [isEvalModalVisible, setIsEvalModalVisible] = useState(false);
  const [evalCriteriaIds, setEvalCriteriaIds] = useState<number[]>([]);
  const [isEvalRunning, setIsEvalRunning] = useState(false);
  const [progress, setProgress] = useState<ProgressData | null>(null);
  const [selectedCriteria, setSelectedCriteria] = useState<string | null>(null);

  useEffect(() => {
    if (intentGroupId) {
      IntentService.getIntentGroup(
        Number(intentGroupId),
        false,
        setIsLoading,
        apiKey
      ).subscribe(
        (ig: IntentGroup) => setIntentGroup(ig),
        (error: any) => console.error(error)
      );
    }
  }, [intentGroupId]);

  useEffect(() => {
    if (testSetId) {
      TestSetService.getTestSet(testSetId, null, apiKey).subscribe(
        (data: TestSet) => setTestSet(data),
        (error: any) => {
          console.error(error);
          notification.error({
            message: "Error",
            description: `Failed to fetch test set: ${error}`,
          });
        }
      );
    }
  }, [testSetId]);

  useEffect(() => {
    if (testRunId) {
      setIsLoading(true);
      TestRunService.getTestRun(
        Number(testRunId),
        setIsLoading,
        apiKey
      ).subscribe(
        (data: TestRun) => setTestRun(data),
        (error: any) => {
          console.error(error);
          notification.error({
            message: "Error",
            description: `Failed to fetch test run: ${error}`,
          });
          setIsLoading(false);
        }
      );
    }
  }, [testRunId]);

  useEffect(() => {
    if (!testRunId || !testRun) return;
    const status = testRun.status?.toUpperCase();
    if (status === "COMPLETED" || status === "ERROR") return;

    const fetchProgress = () => {
      TestRunService.getTestRunProgress(
        Number(testRunId),
        () => {},
        apiKey
      ).subscribe(
        (data: ProgressData) => {
          setProgress(data);
          if (data.status === "COMPLETED" || data.status === "ERROR") {
            TestRunService.getTestRun(
              Number(testRunId),
              () => {},
              apiKey
            ).subscribe(
              (run: TestRun) => setTestRun(run),
              (err: any) => console.error("Failed to fetch test run:", err)
            );
            TestRunService.getTestRunResults(
              Number(testRunId),
              () => {},
              apiKey
            ).subscribe(
              (res: any) => setTestRunResults(res),
              (err: any) =>
                console.error("Failed to fetch test run results:", err)
            );
            setRequestsByPage({});
          }
        },
        () => {}
      );
    };

    fetchProgress();
    const interval = setInterval(fetchProgress, 60_000);
    return () => clearInterval(interval);
  }, [testRunId, testRun?.status, apiKey]);

  const fetchRequests = (page: number) => {
    const offset = (page - 1) * 10;
    setIsRequestsLoading(true);
    TestRunService.getTestRunRequests(
      Number(testRunId),
      offset,
      10,
      setIsRequestsLoading,
      apiKey,
      selectedScores.length > 0 ? selectedScores : null,
      selectedCriteria
    ).subscribe(
      (response: { items: TestRunRequest[]; total: number }) => {
        setRequests(response.items);
        setTotalRequests(response.total);
        setRequestsByPage((prev) => ({ ...prev, [page]: response.items }));
        setIsRequestsLoading(false);
      },
      (error: any) => {
        console.error(error);
        notification.error({
          message: "Error",
          description: `Failed to fetch requests: ${error}`,
        });
        setIsRequestsLoading(false);
      }
    );
  };

  useEffect(() => {
    if (testRunId && !requestsByPage[currentPage]) {
      fetchRequests(currentPage);
    } else {
      setRequests(requestsByPage[currentPage]);
    }
  }, [testRunId, currentPage, requestsByPage, selectedScores, selectedCriteria]);

  useEffect(() => {
    if (testRunId) {
      setIsResultsLoading(true);
      TestRunService.getTestRunResults(
        Number(testRunId),
        setIsResultsLoading,
        apiKey
      ).subscribe(
        (data: any) => {
          setTestRunResults(data);
          setIsResultsLoading(false);
        },
        (error: any) => {
          console.error(error);
          notification.error({
            message: "Error",
            description: `Failed to fetch test run results: ${error}`,
          });
          setIsResultsLoading(false);
        }
      );
    }
  }, [testRunId]);

  const handleCompareClick = (requestId: number) => {
    setSelectedComparisonRequestId(requestId);
    setIsComparisonModalVisible(true);
  };

  const handleRowClick = (record: TestRunRequest) => {
    handleCompareClick(record.id!);
  };

  const handleScoreClick = (score: number) => {
    setSelectedScores((prev) => {
      const next = prev.includes(score)
        ? prev.filter((s) => s !== score)
        : [...prev, score];
      setRequestsByPage({});
      return next;
    });
  };

  const handleScoreFilterClear = () => {
    setSelectedScores([]);
    setRequestsByPage({});
  };

  const handleCriteriaSelect = (criteria: string | null) => {
    setSelectedCriteria(criteria);
    setSelectedScores([]);
    setRequestsByPage({});
    setCurrentPage(1);
  };

  const handleRunEvaluations = () => {
    setIsEvalRunning(true);
    TestRunService.evaluateTestRun(
      Number(testRunId),
      evalCriteriaIds,
      setIsEvalRunning,
      apiKey
    ).subscribe(
      () => {
        notification.success({
          message: "Evaluations started",
          description:
            "The selected evaluations are running in the background. Refresh to see results.",
        });
        setIsEvalModalVisible(false);
        setIsEvalRunning(false);
      },
      (err: any) => {
        notification.error({
          message: "Failed to start evaluations",
          description: err.message,
        });
        setIsEvalRunning(false);
      }
    );
  };

  return {
    intentGroupId, testSetId, testRunId, apiKey,
    intentGroup, testSet, testRun, isLoading,
    requests, isRequestsLoading,
    currentPage, setCurrentPage, totalRequests,
    testRunResults, isResultsLoading,
    selectedScores, selectedCriteria, progress,
    isConfigModalVisible, setIsConfigModalVisible,
    isComparisonModalVisible, setIsComparisonModalVisible,
    selectedComparisonRequestId, setSelectedComparisonRequestId,
    selectedRequestId, setSelectedRequestId,
    isRepresentationModalVisible, setIsRepresentationModalVisible,
    isEvalModalVisible, setIsEvalModalVisible,
    evalCriteriaIds, setEvalCriteriaIds, isEvalRunning,
    handleRowClick, handleScoreClick, handleCompareClick,
    handleScoreFilterClear, handleCriteriaSelect, handleRunEvaluations,
  };
}
